import type { ClientOrganization } from '@DigitaleDoerfer/digitale-doerfer-api';
import {
  ClientOrganizationRoleEnum,
  ClientOrganizationTagsEnum
} from '@DigitaleDoerfer/digitale-doerfer-api';
import type { FC } from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';
import { Text } from 'react-native-paper';

import { AccountBalanceIcon } from '@/assets';
import { ListItem } from '@/features/list/components/ListItem';
import { ListSection } from '@/features/list/components/ListSection';
import { ProfileSettingRoute } from '@/features/profile-setting/navigation/profileSettingRoute';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { useAppNavigation } from '@/navigation/hooks/useAppNavigation';
import { OrganizationAccessGuard } from '@/shared/components/organization';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

type Props = {
  organization: ClientOrganization | undefined;
  territorialOrganizations: ClientOrganization[] | undefined;
};

export const OrganizationDetailAreaSection: FC<Props> = ({
  organization,
  territorialOrganizations
}) => {
  const theme = useAppTheme();
  const navigation = useAppNavigation();
  const { t } = useTranslation(LocalizationNameSpace.ORGANIZATION, {
    keyPrefix: 'organization-detail'
  });
  const rowBackground = theme.colors.surfaceContainerLowest;

  const handleOrganizationsPress = useCallback(() => {
    if (!organization) return;
    navigation.navigate(ProfileSettingRoute.OrganizationsInArea, {
      organizationId: organization.id
    });
  }, [navigation, organization]);

  return (
    <OrganizationAccessGuard
      requiredTagsAny={[
        ClientOrganizationTagsEnum.TerritorialAuthority,
        ClientOrganizationTagsEnum.Admin
      ]}
      roles={[
        ClientOrganizationRoleEnum.Admin,
        ClientOrganizationRoleEnum.Funker
      ]}
    >
      <ListSection
        title={t('sections.area')}
        style={styles.section}
        headerBackgroundColor="transparent"
      >
        <ListItem
          title={t('rows.organizations')}
          icon={AccountBalanceIcon}
          backgroundColor={rowBackground}
          badge={
            territorialOrganizations ? (
              <Text variant="bodyMedium">
                {territorialOrganizations.length}
              </Text>
            ) : undefined
          }
          onPress={handleOrganizationsPress}
        />
      </ListSection>
    </OrganizationAccessGuard>
  );
};

const styles = StyleSheet.create({
  section: { backgroundColor: 'transparent' }
});
